import React, { useState } from "react";
import { Car, Trash2, Search, RefreshCw } from "lucide-react";
import { Button } from "../shared/Button";
import { vehiculos as api } from "../../api/modulos.service";
import { coincide, coincidePlaca } from "../../utils/texto";
import { estilos as est, Cabecera, Buscador, Vacio, Aviso, Ventana, Entrada, Seleccion, useCarga } from "./base";

/**
 * VEHÍCULOS DE LA RESIDENCIAL.
 *
 * El residente ve y administra los de su casa; la administración, todos; el
 * guardia consulta —necesita saber si la placa que tiene delante es de
 * adentro— pero no edita: no es su decisión de quién es un carro.
 */

const FORM_VACIO = { plate: "", brand: "", model: "", year: "", color: "", kind: "carro", ownerName: "", houseNumber: "", polygon: "", notes: "" };

export default function Vehiculos({ rol, catalogos }) {
  const [busqueda, setBusqueda] = useState("");
  const [abierta, setAbierta] = useState(false);
  const [form, setForm] = useState(FORM_VACIO);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const { datos, cargando, error: errorCarga, recargar } = useCarga(() => api.listar({}), []);
  const lista = datos?.vehicles || [];
  const puedeAdministrar = rol === "resident" || rol === "admin";

  const filtrados = busqueda
    ? lista.filter(
        (v) =>
          // La placa se compara COMO PLACA: se guarda sin guiones, así que
          // «P123-456» tiene que encontrar a «P123456».
          coincidePlaca(v.plate, busqueda) ||
          [v.brand, v.model, v.ownerName, v.houseNumber]
            .filter(Boolean)
            .some((c) => coincide(c, busqueda))
      )
    : lista;

  const cambiar = (c) => (e) => setForm({ ...form, [c]: e.target.value });

  const guardar = async (e) => {
    e.preventDefault();
    setError("");
    setGuardando(true);
    try {
      await api.crear({ ...form, year: form.year || undefined });
      setAbierta(false);
      setForm(FORM_VACIO);
      recargar();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.errors?.[0]?.msg || "No se pudo guardar");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={est.seccion}>
      <Cabecera
        titulo="Vehículos"
        subtitulo={rol === "guard" ? "Consulte si una placa es de adentro." : "Los carros de su casa."}
        accion={puedeAdministrar ? "Registrar vehículo" : null}
        alAccionar={() => setAbierta(true)}
        icono={Car}
      />

      <Buscador valor={busqueda} alCambiar={setBusqueda} marcador="Buscar por placa, marca o dueño…" />

      {error && <Aviso>{error}</Aviso>}

      {errorCarga ? (
        <Aviso>
          {errorCarga}
          <div style={{ marginTop: "var(--space-3)" }}>
            <Button size="sm" variant="outline" leftIcon={RefreshCw} onClick={recargar}>
              Reintentar
            </Button>
          </div>
        </Aviso>
      ) : cargando ? (
        <Vacio>Cargando…</Vacio>
      ) : filtrados.length === 0 ? (
        <Vacio>{busqueda ? "Ninguna placa coincide." : "Todavía no hay vehículos registrados."}</Vacio>
      ) : (
        <div style={est.rejilla}>
          {filtrados.map((v) => (
            <div key={v._id} style={est.tarjeta}>
              <h3 style={{ ...est.tarjetaTitulo, letterSpacing: "0.06em" }}>{v.plate}</h3>
              <div style={est.tarjetaDato}>
                {[v.brand, v.model, v.color].filter(Boolean).join(" · ") || "Sin detalles"}
                {v.year ? ` · ${v.year}` : ""}
              </div>
              <div style={est.tarjetaDato}>
                Casa {v.polygon}-{v.houseNumber}
                {v.ownerName ? ` · ${v.ownerName}` : ""}
              </div>
              {puedeAdministrar && (
                <div style={est.tarjetaAcciones}>
                  <Button
                    size="sm"
                    variant="ghost"
                    leftIcon={Trash2}
                    onClick={() => {
                      if (window.confirm(`¿Dar de baja el vehículo ${v.plate}?`)) {
                        api.darDeBaja(v._id).then(recargar);
                      }
                    }}
                  >
                    Dar de baja
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Ventana
        titulo="Registrar un vehículo"
        abierta={abierta}
        alCerrar={() => setAbierta(false)}
        pie={
          <>
            <Button variant="ghost" onClick={() => setAbierta(false)}>Cancelar</Button>
            <Button variant="primary" onClick={guardar} loading={guardando}>Registrar</Button>
          </>
        }
      >
        {error && <Aviso>{error}</Aviso>}
        <Entrada
          etiqueta="Placa"
          value={form.plate}
          onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })}
          placeholder="P123-456"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          required
        />
        <div style={est.fila}>
          <Entrada etiqueta="Marca" value={form.brand} onChange={cambiar("brand")} placeholder="Toyota" />
          <Entrada etiqueta="Modelo" value={form.model} onChange={cambiar("model")} placeholder="Corolla" />
        </div>
        <div style={est.fila}>
          <Entrada etiqueta="Color" value={form.color} onChange={cambiar("color")} placeholder="Blanco" />
          <Entrada etiqueta="Año" type="number" value={form.year} onChange={cambiar("year")} placeholder="2020" />
          <Seleccion
            etiqueta="Tipo"
            value={form.kind}
            onChange={cambiar("kind")}
            opciones={catalogos?.tiposVehiculo || [{ valor: "carro", etiqueta: "Carro" }]}
          />
        </div>
        {rol === "admin" && (
          <div style={est.fila}>
            <Entrada etiqueta="Polígono" value={form.polygon} onChange={cambiar("polygon")} required />
            <Entrada etiqueta="Casa" value={form.houseNumber} onChange={cambiar("houseNumber")} required />
            <Entrada etiqueta="Propietario" value={form.ownerName} onChange={cambiar("ownerName")} />
          </div>
        )}
      </Ventana>
    </div>
  );
}
